import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { ApiError } from '@/services/api';
import { chatService } from '@/services/chatService';
import { chatSocket } from '@/services/chatSocket';
import type { ChatScopeKind, InboxFilter } from '@/types/chat';

export const chatQueryKeys = {
  scoped: (kind: ChatScopeKind, scopeId: string) =>
    ['chat', 'scoped', kind, scopeId] as const,
  conversation: (conversationId: string | null) =>
    ['chat', 'conversation', conversationId] as const,
  /** Every inbox list (all filters), for live updates and invalidation. */
  inboxAll: () => ['chat', 'inbox'] as const,
  inbox: (filter: InboxFilter) => ['chat', 'inbox', filter] as const,
  members: (conversationId: string | null) =>
    ['chat', 'members', conversationId] as const,
  online: (conversationId: string | null) =>
    ['chat', 'online', conversationId] as const,
  /** Every cached unread total, for invalidation. */
  unreadAll: () => ['chat', 'unread'] as const,
  unreadTotal: (userId: string | null) => ['chat', 'unread', userId] as const,
  blocks: () => ['chat', 'blocks'] as const,
  people: () => ['chat', 'people'] as const,
  /** The user's own chats the people picker offers before anything is typed. */
  peopleSeed: () => ['chat', 'people', 'seed'] as const,
};

/** A 4xx (no access, bad id) won't change on retry; only transient failures are retried. */
export const retryTransient = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
  failureCount < 2;

/**
 * The conversation id behind a tournament or match chat. Resolved only while `enabled`, because
 * the server creates the conversation on first resolve.
 */
export function useScopedConversationId(
  kind: ChatScopeKind,
  scopeId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: chatQueryKeys.scoped(kind, scopeId),
    queryFn: () => chatService.getScopedConversationId(kind, scopeId),
    enabled: enabled && !!scopeId,
    staleTime: Infinity,
    retry: retryTransient,
  });
}

/** Header data for an open chat: status, member count and the caller's membership (incl. mute). */
export function useChatConversationDetail(conversationId: string | null) {
  return useQuery({
    queryKey: chatQueryKeys.conversation(conversationId),
    queryFn: () => chatService.getConversation(conversationId as string),
    enabled: !!conversationId,
    staleTime: 30_000,
    retry: retryTransient,
  });
}

const subscribeStatus = (onChange: () => void) => chatSocket.onStatus(onChange);
const readStatus = () => chatSocket.getStatus();

/** Live status of the app-wide chat socket. */
export function useChatSocketStatus() {
  return useSyncExternalStore(subscribeStatus, readStatus, readStatus);
}
